import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Database, TrendingUp, FileSpreadsheet } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

export default function InsuranceFees() {
  const [tab, setTab] = useState("verify");
  // Verification
  const [vForm, setVForm] = useState({ payor: "", member_id: "", group_number: "", subscriber_first_name: "", subscriber_last_name: "", subscriber_dob: "", procedure_codes: "D0150,D2740,D6010" });
  const [vResult, setVResult] = useState(null);
  const [vBusy, setVBusy] = useState(false);
  const [verifications, setVerifications] = useState([]);
  // UCR
  const [ucr, setUcr] = useState(null);
  // Schedule upload
  const [payor, setPayor] = useState("");
  const [scheduleName, setScheduleName] = useState("");
  const [file, setFile] = useState(null);
  const [schedules, setSchedules] = useState([]);
  // Optimize
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [optPayor, setOptPayor] = useState("");
  const [optSchedId, setOptSchedId] = useState("");
  const [optResult, setOptResult] = useState(null);
  const [optBusy, setOptBusy] = useState(false);

  useEffect(() => {
    api.get("/fee-schedule/ucr").then((r) => setUcr(r.data)).catch(() => {});
    api.get("/insurance/verify").then((r) => setVerifications(r.data || [])).catch(() => {});
    api.get("/fee-schedule").then((r) => setSchedules(r.data || [])).catch(() => {});
    api.get("/treatment-plan").then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);

  const verify = async () => {
    setVBusy(true);
    try {
      const codes = vForm.procedure_codes.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await api.post("/insurance/verify", { ...vForm, procedure_codes: codes });
      setVResult(r.data);
      const list = await api.get("/insurance/verify");
      setVerifications(list.data);
      toast.success(`Verified (${r.data.mode})`);
    } catch (e) {
      toast.error(errMsg(e, "Verification failed"));
    } finally { setVBusy(false); }
  };

  const uploadSchedule = async (e) => {
    e.preventDefault();
    if (!file || !payor || !scheduleName) { toast.error("Payor, name, and file required"); return; }
    const fd = new FormData();
    fd.append("payor", payor); fd.append("schedule_name", scheduleName); fd.append("file", file);
    try {
      const r = await api.post("/fee-schedule/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.code_count} codes`);
      const list = await api.get("/fee-schedule");
      setSchedules(list.data); setFile(null); setPayor(""); setScheduleName("");
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };

  const optimize = async () => {
    if (!planId) { toast.error("Select a plan"); return; }
    setOptBusy(true);
    try {
      const r = await api.post(`/fee-schedule/optimize/${planId}`, { payor: optPayor || null, practice_fee_schedule_id: optSchedId || null });
      setOptResult(r.data.optimization);
      toast.success("Optimization complete");
    } catch (e) { toast.error(errMsg(e, "Optimization failed")); }
    finally { setOptBusy(false); }
  };

  const iCl = "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2";

  return (
    <Layout>
      <div className="kicker">Phase 4 · Financial Intelligence</div>
      <h1 data-testid="fin-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <Database size={22} className="text-[var(--pa-gold)]" /> Insurance &amp; Fee Schedule
      </h1>

      <div className="mt-6 flex gap-2 border-b border-[var(--pa-border)]">
        {[["verify", "Verify"], ["ucr", "UCR Fees"], ["schedules", "Practice Schedules"], ["optimize", "Optimize"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fin-tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-[var(--pa-gold)] text-[var(--pa-gold)]" : "border-transparent text-[var(--pa-text-muted)] hover:text-[var(--pa-text)]"}`}>{l}</button>
        ))}
      </div>

      {tab === "verify" && (
        <div className="mt-6 grid md:grid-cols-2 gap-3 max-w-3xl">
          <div className="md:col-span-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2.5">
            Availity credentials not configured → running in <span className="font-semibold">MOCK MODE</span>. Activates automatically when AVAILITY_API_KEY is added.
          </div>
          {[["payor", "Payor (e.g. Aetna)"], ["member_id", "Member ID"], ["group_number", "Group #"], ["subscriber_first_name", "Subscriber first name"], ["subscriber_last_name", "Subscriber last name"], ["subscriber_dob", "DOB YYYY-MM-DD"]].map(([k, label]) => (
            <div key={k}><label className="kicker block mb-1">{label}</label>
              <input value={vForm[k]} onChange={(e) => setVForm({ ...vForm, [k]: e.target.value })} data-testid={`v-${k}`} className={iCl} /></div>
          ))}
          <div className="md:col-span-2"><label className="kicker block mb-1">Procedure CDT codes (comma-separated)</label>
            <input value={vForm.procedure_codes} onChange={(e) => setVForm({ ...vForm, procedure_codes: e.target.value })} className={iCl} data-testid="v-codes" /></div>
          <button onClick={verify} disabled={vBusy} data-testid="v-submit"
            className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded flex items-center gap-2 disabled:opacity-60">
            {vBusy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Verify
          </button>
          {vResult && (
            <pre className="md:col-span-2 mt-3 text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3 text-[var(--pa-text)] overflow-auto" data-testid="v-result">
              {JSON.stringify(vResult.result, null, 2)}
            </pre>
          )}
          {verifications.length > 0 && (
            <div className="md:col-span-2 mt-4">
              <div className="kicker mb-2">Recent verifications</div>
              <div className="space-y-1.5">
                {verifications.slice(0, 5).map((v) => (
                  <div key={v.id} className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-2 flex justify-between">
                    <span>{v.payor} · {v.member_id}</span>
                    <span className="text-[var(--pa-text-dim)]">{v.mode} · {new Date(v.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "ucr" && ucr && (
        <div className="mt-6">
          <div className="text-sm text-[var(--pa-text-muted)] mb-3">
            {ucr.region} · {ucr.specialty} · {ucr.percentile}th percentile · {ucr.count} CDT codes pre-loaded
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5" data-testid="ucr-grid">
            {Object.entries(ucr.fees).map(([code, fee]) => (
              <div key={code} className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded px-2 py-1.5 flex justify-between">
                <span className="font-mono text-[var(--pa-gold)]">{code}</span>
                <span className="text-[var(--pa-text)]">${fee}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "schedules" && (
        <div className="mt-6 max-w-2xl">
          <form onSubmit={uploadSchedule} className="space-y-3" data-testid="sched-upload-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="kicker block mb-1">Payor</label>
                <input value={payor} onChange={(e) => setPayor(e.target.value)} className={iCl} data-testid="sched-payor" /></div>
              <div><label className="kicker block mb-1">Schedule name</label>
                <input value={scheduleName} onChange={(e) => setScheduleName(e.target.value)} className={iCl} data-testid="sched-name" /></div>
            </div>
            <input type="file" accept=".csv,.txt" onChange={(e) => setFile(e.target.files[0])} className="text-xs text-[var(--pa-text-muted)]" data-testid="sched-file" />
            <button type="submit" className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2 rounded flex items-center gap-2 text-sm" data-testid="sched-submit">
              <FileSpreadsheet size={14} /> Upload CSV
            </button>
          </form>
          <div className="mt-6">
            <div className="kicker mb-2">Existing schedules</div>
            {schedules.length === 0 ? <div className="text-xs text-[var(--pa-text-dim)]">None yet.</div> :
              schedules.map((s) => (
                <div key={s.id} className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded px-3 py-2 mb-1.5 flex justify-between">
                  <span>{s.payor} · {s.schedule_name} ({s.code_count} codes)</span>
                  <span className="text-[var(--pa-text-dim)]">{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === "optimize" && (
        <div className="mt-6 max-w-3xl">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="kicker block mb-1">Treatment plan</label>
              <select value={planId} onChange={(e) => setPlanId(e.target.value)} className={iCl} data-testid="opt-plan">
                <option value="">— Select —</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.patient_name} ({new Date(p.created_at).toLocaleDateString()})</option>)}
              </select></div>
            <div><label className="kicker block mb-1">Payor (optional)</label>
              <input value={optPayor} onChange={(e) => setOptPayor(e.target.value)} className={iCl} /></div>
            <div><label className="kicker block mb-1">Practice schedule</label>
              <select value={optSchedId} onChange={(e) => setOptSchedId(e.target.value)} className={iCl}>
                <option value="">— UCR only —</option>
                {schedules.map((s) => <option key={s.id} value={s.id}>{s.payor} — {s.schedule_name}</option>)}
              </select></div>
          </div>
          <button onClick={optimize} disabled={optBusy} data-testid="opt-submit"
            className="mt-4 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded flex items-center gap-2 text-sm disabled:opacity-60">
            {optBusy ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />} Optimize
          </button>
          {optResult && (
            <div className="mt-6 space-y-4" data-testid="opt-result">
              <div className="bg-[var(--pa-gold)]/5 border border-[var(--pa-gold)]/40 rounded p-4">
                <div className="kicker text-[var(--pa-gold)]">Total revenue opportunity</div>
                <div className="font-serif text-3xl text-[var(--pa-gold)] mt-1">${(optResult.total_revenue_opportunity_usd || 0).toLocaleString()}</div>
                <p className="text-sm text-[var(--pa-text-muted)] mt-2">{optResult.summary}</p>
              </div>
              {["underpriced_codes", "missed_bundling_opportunities", "narrative_required_codes", "downgrade_risks"].map((k) => (
                optResult[k]?.length > 0 && (
                  <div key={k}>
                    <div className="kicker mb-2">{k.replace(/_/g, " ")}</div>
                    <pre className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3 text-[var(--pa-text)] whitespace-pre-wrap font-mono overflow-auto">
                      {JSON.stringify(optResult[k], null, 2)}
                    </pre>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
